import React, { useCallback, useRef, useState, useEffect, RefObject } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import styled from 'styled-components';

import { updateMmsContentDimensions } from '@/redux/app/actions/event';
import { toggleZoomedInMediaModal } from '@/redux/app/actions/inbox';
import { ReactNative } from '@/api/app/react-native';

import vcard from ':assets/images/vcard.png';
import pdf from ':assets/images/pdf.png';

import useLongPress from '../shared/LongPress';
import { Video } from '../ui-elements/Media/Video';

export interface MmsContentProps {
  mime: string;
  url: string;
  ticket: string;
  eventId: string;
  dimensions?: { w: number, h: number };
}

interface MmsContentOfTypeProps {
  /* eslint-disable react/require-default-props,react/no-unused-prop-types */
  mime: string;
  url: string;
  handleLoaded?: () => void;
  dimensions?: { w: number, h: number };
  /* eslint-enable react/require-default-props,react/no-unused-prop-types */
}

const DivWithSetDimensions = styled.div<{ height?: number, width?: number }>`
  ${({ height }) => (typeof height === 'number' ? `height: ${height}px;` : '')}
  ${({ width }) => (typeof width === 'number' ? `width: ${width}px;` : '')}
`;

const MmsContentImage = (
  { url, mime, handleLoaded, dimensions, imageRef = useRef() }: MmsContentOfTypeProps
  &{ imageRef?: RefObject<HTMLImageElement> },
) => {
  const [noZoom, setNoZoom] = useState(true);
  const handleLoad = useCallback(() => {
    const { naturalWidth, naturalHeight, width, height } = imageRef.current;
    const isMediaBiggerThanContainer = naturalWidth > width || naturalHeight > height;
    if (isMediaBiggerThanContainer === noZoom) setNoZoom(!isMediaBiggerThanContainer);
    handleLoaded();
  }, [noZoom, handleLoaded]);

  const containerRef = useRef<HTMLDivElement>();
  useEffect(() => {
    if (!dimensions) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    const isMediaBiggerThanContainer = dimensions.w > width || dimensions.h > height;
    if (isMediaBiggerThanContainer === noZoom) setNoZoom(!isMediaBiggerThanContainer);
  }, [dimensions]);

  const dispatch = useDispatch();
  const longPressAction = () => ReactNative.saveImage(url);
  const handleLongPress = useLongPress(longPressAction, 500);
  const handleZoomIn = useCallback(() => {
    dispatch(toggleZoomedInMediaModal({ url, mime, dimensions }));
  }, [url, mime]);

  const heightConstraint = dimensions?.h && Math.min(dimensions.h, 250);
  const widthConstraint = dimensions?.w && (
    heightConstraint === dimensions.h ? dimensions.w : 250 * (dimensions.w / dimensions.h)
  );

  return (
    <DivWithSetDimensions
      ref={containerRef}
      height={heightConstraint}
      width={widthConstraint}
      className={classNames('image-container', noZoom && 'no-zoom')}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <img
        ref={imageRef}
        src={url}
        alt=""
        onLoad={handleLoad}
        onClick={!noZoom ? handleZoomIn : null}
        onKeyPress={!noZoom ? handleZoomIn : null}
        height={heightConstraint}
        style={{ objectFit: 'cover' }}
        {...handleLongPress}
      />
    </DivWithSetDimensions>
  );
};

const MmsContentAudio = ({ mime, url }: MmsContentOfTypeProps) => (
  // eslint-disable-next-line jsx-a11y/media-has-caption
  <audio controls>
    <source src={url} type={mime} />
    <a target="_blank" rel="noreferrer" href={url}>
      {`Download Audio attachment: ${url.split('/').pop()}`}
    </a>
  </audio>
);

const MmsContentVideo = ({ mime, url }: MmsContentOfTypeProps) => (
  <Video height={140} mime={mime} url={url} />
);

const MmsContentContact = ({ url }: MmsContentOfTypeProps) => (
  <>
    <div className="vcard-container">
      <a href={url} target="_blank" rel="noreferrer">
        <img className="vcard-icon" src={vcard} alt="Contact icon" />
      </a>
    </div>
    <a target="_blank" rel="noreferrer" href={url}>Download vCard</a>
  </>
);

const MmsContentPdf = ({ url }: MmsContentOfTypeProps) => (
  <>
    <div className="pdf-container">
      <a href={url} target="_blank" rel="noreferrer">
        <img className="pdf-icon" src={pdf} alt="PDF icon" />
      </a>
    </div>
    <a target="_blank" rel="noreferrer" href={url}>Download PDF</a>
  </>
);

const MmsContentFile = ({ mime, url }: MmsContentOfTypeProps) => (
  <a target="_blank" rel="noreferrer" href={url}>
    {`Download ${mime} Attachment: ${url.split('/').pop()}`}
  </a>
);

export const MmsContent = ({ ticket, eventId, mime, url, dimensions }: MmsContentProps) => {
  const imageRef = useRef<HTMLImageElement>();
  const dispatch = useDispatch();
  const handleLoadedImage = useCallback(() => {
    if (dimensions) return;
    const { current: image } = imageRef;
    if (!image) return;
    const { naturalWidth: w, naturalHeight: h } = image;
    if (h === dimensions?.h && w === dimensions?.w) return;
    dispatch(updateMmsContentDimensions(ticket, eventId, url, { w, h }));
  }, [url]);

  if (mime.substr(0, 5) === 'image') {
    return (
      <MmsContentImage
        imageRef={imageRef}
        mime={mime}
        url={url}
        handleLoaded={handleLoadedImage}
        dimensions={dimensions}
      />
    );
  }
  if (mime.substr(0, 5) === 'audio') {
    return <MmsContentAudio mime={mime} url={url} />;
  }
  if (mime.substr(0, 5) === 'video') {
    return <MmsContentVideo mime={mime} url={url} />;
  }
  if (mime === 'text/vcard' || mime === 'text/dictionary') {
    return <MmsContentContact mime={mime} url={url} />;
  }
  if (mime === 'application/pdf') {
    return <MmsContentPdf mime={mime} url={url} />;
  }
  return <MmsContentFile mime={mime} url={url} />;
};
